import type { StudyLog, UUID, Subject, LocalDate } from './index';

export type InclusiveRange = { start: number; end: number };

function assertRange(range: InclusiveRange): void {
  if (
    !Number.isInteger(range.start) ||
    !Number.isInteger(range.end) ||
    range.start < 1 ||
    range.end < range.start
  ) {
    throw new RangeError('Study ranges must be inclusive positive integers with start <= end');
  }
}

export function mergeInclusiveRanges(ranges: readonly InclusiveRange[]): InclusiveRange[] {
  for (const range of ranges) assertRange(range);
  const sorted = [...ranges].sort(
    (left, right) => left.start - right.start || left.end - right.end,
  );
  const merged: InclusiveRange[] = [];
  for (const range of sorted) {
    const previous = merged.at(-1);
    if (!previous || range.start > previous.end + 1) {
      merged.push({ ...range });
      continue;
    }
    previous.end = Math.max(previous.end, range.end);
  }
  return merged;
}

export function inclusiveRangeLength(ranges: readonly InclusiveRange[]): number {
  return mergeInclusiveRanges(ranges).reduce((sum, range) => sum + range.end - range.start + 1, 0);
}

export function missingInclusiveRanges(
  ranges: readonly InclusiveRange[],
  totalUnits: number,
): InclusiveRange[] {
  if (!Number.isInteger(totalUnits) || totalUnits < 1) {
    throw new RangeError('totalUnits must be a positive integer');
  }
  const covered = mergeInclusiveRanges(
    ranges
      .filter((range) => range.start <= totalUnits)
      .map((range) => ({ start: range.start, end: Math.min(range.end, totalUnits) })),
  );
  const missing: InclusiveRange[] = [];
  let cursor = 1;
  for (const range of covered) {
    if (cursor < range.start) missing.push({ start: cursor, end: range.start - 1 });
    cursor = range.end + 1;
  }
  if (cursor <= totalUnits) missing.push({ start: cursor, end: totalUnits });
  return missing;
}

export type RoundProgress = {
  coveredUnits: number;
  totalUnits: number;
  percentage: number;
  ranges: InclusiveRange[];
  requiresReview: boolean;
  outOfBoundsLogIds: UUID[];
};

export function calculateRoundProgress(
  logs: readonly StudyLog[],
  subjectId: UUID,
  round: number,
  totalUnits: number,
): RoundProgress {
  if (!Number.isInteger(round) || round < 1) throw new RangeError('round must be positive');
  if (!Number.isInteger(totalUnits) || totalUnits < 1) {
    throw new RangeError('totalUnits must be positive');
  }
  const relevant = logs.filter(
    (log) => log.subjectId === subjectId && log.round === round && log.deletedAt === undefined,
  );
  const outOfBoundsLogIds = relevant
    .filter((log) => log.unitStart < 1 || log.unitEnd > totalUnits || log.unitStart > log.unitEnd)
    .map((log) => log.id);
  const ranges = mergeInclusiveRanges(
    relevant
      .filter(
        (log) => log.unitEnd >= 1 && log.unitStart <= totalUnits && log.unitStart <= log.unitEnd,
      )
      .map((log) => ({
        start: Math.max(1, log.unitStart),
        end: Math.min(totalUnits, log.unitEnd),
      })),
  );
  // `ranges` is already normalized above. Re-merging it through
  // inclusiveRangeLength would sort the same hot-path data a second time.
  const coveredUnits = ranges.reduce((sum, range) => sum + range.end - range.start + 1, 0);
  return {
    coveredUnits,
    totalUnits,
    percentage: Math.min(100, (coveredUnits / totalUnits) * 100),
    ranges,
    requiresReview: outOfBoundsLogIds.length > 0,
    outOfBoundsLogIds,
  };
}

export function forecastRounds(input: {
  logs: readonly StudyLog[];
  subjects: readonly Subject[];
  today: LocalDate;
  remainingDays: number;
  targetRounds: number;
}) {
  if (input.subjects.some((subject) => subject.totalUnits === null) || !input.subjects.length)
    return undefined;
  const total = input.subjects.reduce((sum, subject) => sum + subject.totalUnits!, 0);
  const first = new Date(`${input.today}T00:00:00Z`).valueOf() - 13 * 86400000;
  const active = input.logs.filter(
    (log) => !log.deletedAt && input.subjects.some((subject) => subject.id === log.subjectId),
  );
  const completed = input.subjects.reduce(
    (sum, subject) =>
      sum +
      [
        ...new Set(active.filter((log) => log.subjectId === subject.id).map((log) => log.round)),
      ].reduce(
        (subtotal, round) =>
          subtotal +
          calculateRoundProgress(active, subject.id, round, subject.totalUnits!).coveredUnits,
        0,
      ),
    0,
  );
  const recent = active
    .filter(
      (log) => Date.parse(`${log.studyDate}T00:00:00Z`) >= first && log.studyDate <= input.today,
    )
    .reduce((sum, log) => sum + log.unitEnd - log.unitStart + 1, 0);
  if (!recent) return undefined;
  const daily = recent / 14;
  return {
    rounds: (completed + daily * Math.max(0, input.remainingDays)) / total,
    extraPerDay:
      input.remainingDays > 0
        ? Math.max(
            0,
            Math.ceil((input.targetRounds * total - completed) / input.remainingDays - daily),
          )
        : 0,
  };
}
